import React from 'react';
import {
  Task,
  TaskTimeline,
  TaskTimelineFrameEnum,
  TaskTimelineRow,
} from 'graphql-common';
import { TFunction } from 'i18next';
import classNames from 'classnames';
import _get from 'lodash/get';
import _filter from 'lodash/filter';
import { v4 as uuidv4 } from 'uuid';
import { getDaysInMonth } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import {
  BaseItem,
  CellContentAlign,
  CellProps,
  Column,
} from '@lib/components/Table/types';
import StatusBox from '@lib/components/StatusBox/StatusBox';
import ValidityHint from '@lib/enums/validityHint';
import { CET, revertTransformDate } from '@lib/utils/dateTimeHelpers';
import styles from './columns.module.scss';

const getDayId = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const getColumnsData = (
  view: TaskTimelineFrameEnum,
  t: TFunction,
  endDate: Date,
) => {
  const columnsData: {
    name: string;
    subName?: string;
    id: string;
    isWeekend?: boolean;
  }[] = [];

  const totalColumns = {
    [TaskTimelineFrameEnum.Year]: 12,
    [TaskTimelineFrameEnum.Quarter]: 3,
    [TaskTimelineFrameEnum.Month]: getDaysInMonth(endDate),
    [TaskTimelineFrameEnum.Week]: 7,
  }[view];

  const currentDate = new Date(endDate);
  if (view === TaskTimelineFrameEnum.Month) {
    currentDate.setDate(1);
  } else if (view === TaskTimelineFrameEnum.Week) {
    const currentDayOfWeek = currentDate.getDay();
    const diffToMonday = (currentDayOfWeek + 6) % 7; // Difference to Monday
    currentDate.setDate(currentDate.getDate() - diffToMonday);
  }

  const columnsYear = endDate.getFullYear();
  const startMonth =
    view === TaskTimelineFrameEnum.Quarter
      ? Math.floor(endDate.getMonth() / 3) * 3
      : 0;

  for (let i = 0; i < totalColumns; i += 1) {
    if (
      view === TaskTimelineFrameEnum.Year ||
      view === TaskTimelineFrameEnum.Quarter
    ) {
      const monthDate = new Date(columnsYear, startMonth + i, 1);
      const monthName = t(`month-${monthDate.getMonth() + 1}`);
      columnsData.push({
        name: `${monthName}`,
        id: `${monthDate.getFullYear()}-${String(monthDate.getMonth() + 1).padStart(2, '0')}`,
      });
    } else {
      const weekDay = currentDate.getDay();
      const dayName = t(`weekday-${weekDay}`);
      const monthName = t(`month-${currentDate.getMonth() + 1}`);
      columnsData.push({
        isWeekend: weekDay === 0 || weekDay === 6,
        subName: dayName,
        name: `${monthName} ${currentDate.getDate()}`,
        id: getDayId(currentDate),
      });
      currentDate.setDate(currentDate.getDate() + 1);
    }
  }

  return columnsData;
};

const getFilteredTimelineRows = (
  item: TaskTimeline & BaseItem,
  id: string,
): TaskTimelineRow[] => {
  const rows = _get(item, 'timeline.rows', []) as TaskTimelineRow[];
  return _filter(rows, (row) => row.date === id);
};

function TimelineDayCell(props: { filteredRows: TaskTimelineRow[] }) {
  const { filteredRows } = props;
  const boxes = filteredRows.map(({ validityHintCounters }) => (
    <div className={styles.rowStatusBoxes} key={uuidv4()}>
      {validityHintCounters.none ? (
        <StatusBox text={validityHintCounters.none} type={ValidityHint.None} />
      ) : null}
      {validityHintCounters.failure ? (
        <StatusBox
          text={validityHintCounters.failure}
          type={ValidityHint.Failure}
        />
      ) : null}
      {validityHintCounters.warning ? (
        <StatusBox
          text={validityHintCounters.warning}
          type={ValidityHint.Warning}
        />
      ) : null}
      {validityHintCounters.success ? (
        <StatusBox
          text={validityHintCounters.success}
          type={ValidityHint.Success}
        />
      ) : null}
    </div>
  ));

  return <div>{boxes}</div>;
}

export const getAdditionalCell = (id: string) =>
  function TimelineAdditionalCell(props: CellProps<TaskTimeline & BaseItem>) {
    const { item } = props;
    const filteredRows = getFilteredTimelineRows(item, id);

    if (!filteredRows.length) return null;
    return <TimelineDayCell filteredRows={filteredRows} />;
  };

export const getAdditionalCellOnClick =
  (id: string, onCellClick?: (task: Task, bucketId: string) => void) =>
  (item: TaskTimeline & BaseItem) => {
    if (!onCellClick) return undefined;
    const filteredRows = getFilteredTimelineRows(item, id);
    // Sum the counters by name — `Object.values` would also pick up Apollo's
    // `__typename` string.
    const hasCompletions = filteredRows.some(
      ({ validityHintCounters: { none, failure, warning, success } }) =>
        !!(none || failure || warning || success),
    );
    return hasCompletions
      ? () => onCellClick(item as unknown as Task, id)
      : undefined;
  };

type AdditionalColumnsArgs = {
  endDateParam: string | null | undefined;
  onCellClick?: (task: Task, bucketId: string) => void;
  t: TFunction<'translation', undefined>;
  view: TaskTimelineFrameEnum;
};

export const getAdditionalColumns = ({
  endDateParam,
  onCellClick,
  t,
  view,
}: AdditionalColumnsArgs) => {
  const nowDate = endDateParam
    ? revertTransformDate(endDateParam)
    : toZonedTime(new Date(), CET);
  const columns: Column<TaskTimeline & BaseItem>[] = [];
  const columnsData = getColumnsData(view, t, nowDate);
  const todayId = getDayId(toZonedTime(new Date(), CET));
  // Year/Quarter column ids are `yyyy-MM`.
  const currentMonthId = todayId.slice(0, 7);
  // eslint-disable-next-line no-restricted-syntax
  for (const { name, subName, id, isWeekend } of columnsData) {
    const isDayColumn = !!subName;
    const isCurrent = id === (isDayColumn ? todayId : currentMonthId);
    let Header: React.ReactNode = name;
    if (isDayColumn) {
      Header = (
        <div
          className={classNames(styles.dayCellHead, {
            [styles.dayCellHeadToday]: isCurrent,
          })}
        >
          <div>{subName}</div>
          <div>{name}</div>
        </div>
      );
    } else if (isCurrent) {
      Header = <div className={styles.monthCellHeadCurrent}>{name}</div>;
    }
    const column = {
      id: `column-${id}`,
      Header,
      isCustomHeader: isDayColumn || isCurrent,
      cellClassName: isWeekend ? styles.weekendCell : undefined,
      contentAlign: CellContentAlign.Center,
      Cell: getAdditionalCell(id),
      onCellClick: getAdditionalCellOnClick(id, onCellClick),
      minWidth: 124,
      bordered: true,
    };
    columns.push(column);
  }
  return columns;
};
